// THE LOCKED LABELS FOR EVERY SESSION-DERIVED METRIC ON THE ANALYTICS SCREENS.
//
// mAiSight stores no reader identifier of any kind — not a raw one, not a
// hashed one, not an "anonymized" one (CLAUDE.md §Non-goals; the backend's
// Wave 1 handoff repeats it per column). Everything downstream of that is a
// naming problem, and naming is where it gets violated: `unique_sessions`
// reads like a people-count and is not one.
//
// So the words live here, once, and every panel imports them. Changing a
// string in this file is a product decision, not a copy tweak.
//
//   unique_sessions          -> "sessions", "visits". NEVER users / readers /
//                               visitors / unique readers / people.
//   median_session_length_ms -> "median visit length". It is the length of ONE
//                               visit. There is no returning-reader measure and
//                               there cannot be one.
//   bounced_share            -> "bounced" — the share of VISITS that opened no
//                               term. `ENGAGED_LABEL` is its complement, still
//                               per visit.
//
// Permanently unanswerable, and deliberately absent from every screen here:
// returning-reader rate, per-reader retention, cross-session cohorts, unique
// *people*, and any funnel spanning two visits. They are the moat, not a gap —
// do not draw a placeholder or a "coming soon" tile for one.

// ---- THE THREE AXES --------------------------------------------------------
//
// Every panel on this screen belongs to exactly one of them, and the screen is
// grouped by them. The grouping is the product decision that replaced
// `confusion_score`: a single number that mixed engagement with rejection was
// indefensible, so the signals are reported separately and named for what they
// are.
//
//   USAGE      — is the glossary being used, and where.
//   ENGAGEMENT — how deeply. Dwell lives here, as its own number, and it is
//                never multiplied into a usage rank.
//   COUPLING   — how concepts relate to one another across the corpus.

export const AXIS_USAGE_TITLE = "Usage";
export const AXIS_USAGE_BLURB =
  "Whether the glossary is being used, and which terms readers open.";

export const AXIS_ENGAGEMENT_TITLE = "Engagement";
export const AXIS_ENGAGEMENT_BLURB =
  "How deeply visits read — how long they spend on a term, and whether they come back to it quicker.";

export const AXIS_COUPLING_TITLE = "Coupling";
export const AXIS_COUPLING_BLURB =
  "How your concepts relate: what a change would reach, what assumes what, and what readers open together.";

/** Rendered wherever a metric is genuinely absent. Never "0", never "0%",
 *  never a bare em dash: a reader cannot tell a measured zero from a missing
 *  measurement, and `share_of_section_views` is legitimately NULL on any
 *  document that has recorded no `section_view` events. */
export const NOT_MEASURED = "not measured";

/** Why it is not measured, on the element's `title`. */
export const NOT_MEASURED_WHY =
  "No section views have been recorded for this document, so there is no denominator to divide by.";

export const SESSIONS_LABEL = "sessions";
export const SESSIONS_TILE_LABEL = "Sessions observed";
export const MEDIAN_VISIT_LABEL = "median visit";
export const MEDIAN_VISIT_TILE_LABEL = "Median visit length";
export const BOUNCED_LABEL = "bounced";
export const ENGAGED_LABEL = "engaged";
export const SECTION_SHARE_LABEL = "of section views";

/** The sentence that appears once per screen wherever session counts are shown.
 *  Says "we don't collect it", never "we anonymize it" — the distinction is
 *  legally load-bearing (CLAUDE.md §Non-goals). */
export const SESSIONS_ARE_VISITS =
  "Sessions count visits, never people — mAiSight collects no reader identifier of any kind.";

/** Long-form for a `title` attribute on the engaged pill, so nobody reads it
 *  as a share of *people*. */
export const ENGAGED_WHY =
  "Share of visits that opened at least one term. The complement of bounced share; still per visit, never per person.";

// ---- A1, the Fix-and-Prove ledger (PRD 16 §4) ------------------------------
//
// This panel is the one a docs manager forwards to their VP, so its wording
// carries more weight than the rest of the screen: it is the only place the
// product claims an edit CAUSED an improvement. Two rules hold it honest.
//
//  1. The delta is a change in look-ups PER VISIT. Saying "looked up 62% less"
//     without that qualifier lets a drop in traffic read as a win. The label
//     travels with the number everywhere it is rendered.
//  2. A missing verdict is `NOT_MEASURED` with the backend's own reason. Never
//     0%, never "no change", never a hidden row — an edit we cannot yet judge
//     is a fact about the data, and dropping it would overstate the hit rate of
//     the rows that remain.

export const LEDGER_TITLE = "Fix and prove";

/** Sits under the panel header. States the causal limit in the product's own
 *  voice so nobody has to infer it from a tooltip. */
export const LEDGER_SUBTITLE =
  "Definitions your team rewrote, and what reader behaviour did either side of the change.";

/** The qualifier that must accompany every delta on this panel. */
export const PER_VISIT_LABEL = "per visit";

export const LOOKUPS_FELL = "looked up less often";
export const LOOKUPS_ROSE = "looked up more often";
export const LOOKUPS_FLAT = "no material change";

export const BEFORE_LABEL = "before";
export const AFTER_LABEL = "after";

/** Why a row shows no percentage. Prefixed to the backend's own reason string —
 *  the thresholds live in the backend and are never re-derived here. */
export const NO_VERDICT_WHY =
  "Not enough measured behaviour either side of this edit to report a change:";

/** On the delta's `title`. Says what the number is a ratio of, because a reader
 *  who assumes it is a raw total will over-read it. */
export const DELTA_WHY =
  "Change in look-ups per visit, comparing the days before this edit with the days after. Per visit, so a change in traffic cannot read as a change in how often the term needed looking up.";

/** Shown when the after-window has not finished yet. An early reading is not a
 *  wrong reading, but it must not be presented as a settled one. */
export const WINDOW_INCOMPLETE =
  "This window is still open — the reading will move as more visits land.";

/** The panel's empty state. "No edits recorded" is the correct answer for a
 *  team that has not rewritten anything, and it must not look like a failure:
 *  a naive `term_id` join returns exactly this, so the copy names the real
 *  cause rather than implying the query broke. */
export const LEDGER_EMPTY_HEADLINE = "No definition rewrites yet";
export const LEDGER_EMPTY_BODY =
  "When someone edits a definition in review and publishes it, the change lands here with reader behaviour from either side of it.";

// ---- A4, the section drop-off map (PRD 16 §4) ------------------------------
//
// "41% of readers abandon Quickstart at step 3" is the sentence docs teams
// already report, which is why this panel sells with no education — and why the
// wording has to be more careful than usual, because the product cannot
// actually measure the sentence as written.
//
//  1. It says VISITS, not readers. Same rule as everything else on this screen.
//  2. It says "went no further", not "abandoned". There is no `section_leave`
//     event — an exit is the last section a visit was seen viewing — so the
//     panel reports where reading stopped, never why.
//  3. Reaching the last section and stopping is FINISHING. That row is labelled
//     as the end of the document and is never ranked as a drop-off, or every
//     document in existence would post its own conclusion as its worst problem.

export const DROPOFF_TITLE = "Section drop-off";

export const DROPOFF_SUBTITLE =
  "Where visits stop reading, section by section.";

/** On the panel header, and on each exit share. States the measurement's limit
 *  in the product's own voice rather than leaving it to be inferred. */
export const DROPOFF_EXIT_WHY =
  "The share of visits that reached this section and were not seen viewing a later one. mAiSight records no leave event, so this is where reading stopped — not why it stopped.";

export const REACHED_LABEL = "reached";
export const WENT_NO_FURTHER = "went no further";

/** The end-of-document row. Not a failure, and labelled so nobody reads it as
 *  one. */
export const DROPOFF_ENDS_HERE = "end of the document — stopping here is finishing it";

/** Why a row shows no share. Prefixed to the backend's own reason; the
 *  threshold lives in the backend and is never re-derived here. */
export const DROPOFF_NO_VERDICT_WHY =
  "Not enough visits reached this section to report a share:";

/** Shown once, at panel level, when the document has no parsed structure. The
 *  visit counts stay on screen because they are real; only the reading of them
 *  is unavailable. */
export const DROPOFF_UNORDERED =
  "This document's section order is unavailable, so leaving a section cannot be told apart from reaching the end of it. The visit counts below are measured; the drop-off reading is not.";

// ---- A2, the term look-up leaderboard with trend (PRD 16 §4) ---------------
//
// "Rank is table stakes; the delta arrow and 30-day sparkline are what
// distinguish a subscription from a one-time audit."
//
// WHAT THIS PANEL MEASURES, AND WHY IT IS THE ONLY THING IT MEASURES.
// `hover_count / unique_sessions` — look-ups per visit that opened the term.
// Both halves are counts of things that happened. `unique_sessions` counts the
// visits that opened THIS term, so 1.0 means every one of them opened it once
// and moved on, and above that readers came back to it inside the same visit.
// A definition that lands is opened once, which is why FALLING is good here.
//
// It replaced a `confusion_score` that multiplied hover count by dwell and
// added a 5x abandonment term, so a term ranked high either by being studied
// carefully or by being bounced off — two opposite behaviours summed into one
// number, on a weight nobody tuned. Do not reintroduce a composite. Dwell is
// still shown; it has its own label and it ranks nothing.
//
// Two rules keep the trend from claiming more than the data supports.
//
//  1. **No visits means NO RATE.** A rate with no denominator does not exist,
//     and rendering it `0.0` claims "nobody opened this". Those rows render
//     NOT_MEASURED and carry the backend's reason.
//  2. **The sparkline draws gaps, never a flat line on the axis.** A period
//     with nothing measured is not a period of zero look-ups, and a flat line
//     at zero is the most convincing possible way to say otherwise.

export const LEADERBOARD_TITLE = "Most looked-up terms";

/** Under the panel header. Names the ranking's denominator, because a reader
 *  who assumes it is a raw total will over-read every row. */
export const LEADERBOARD_SUBTITLE =
  "Ranked by look-ups per visit, so a busy page cannot outrank a term readers actually keep returning to.";

/** On the rate's `title`. */
export const LOOKUPS_PER_VISIT_WHY =
  "Look-ups divided by the visits that opened the term. 1.0 means every visit opened it once and moved on; above that, readers came back to it inside the same visit. Per visit, so a change in traffic cannot read as a change in how often it is needed.";

/** On the delta's `title`. The comparison window is stated at the call site,
 *  which is the only place that knows how long it is. */
export const TREND_DELTA_WHY =
  "Change in look-ups per visit against the previous window of the same length.";

/** Why a row shows no rate at all. The cause is an absent denominator, not an
 *  absent reader — this term had no visit to divide by in the window. */
export const NO_LOOKUPS_MEASURED = "no visits opened this term";

export const NO_LOOKUPS_WHY =
  "A look-up rate needs visits to divide by. No visit opened this term in this window, so there is no rate — which is not the same as a rate of zero.";

/** The sparkline's replacement when not one day in the window was measurable.
 *  Drawing a flat line here would be the lie the whole panel guards against. */
export const NO_TREND_LINE = "no measured days in this window";

export const TREND_IMPROVED = "looked up less often than the previous window";
export const TREND_WORSENED = "looked up more often than the previous window";
export const TREND_FLAT = "no material change";

export const LEADERBOARD_EMPTY_HEADLINE = "No term activity in this window";
export const LEADERBOARD_EMPTY_BODY =
  "The leaderboard ranks terms by how often visits open them. Widen the range, or check that the widget is installed on a page that gets traffic.";

// ---- B2, the impact radius (PRD 16 §5) -------------------------------------
//
// "Change this term and 14 documents must change with it." The radius comes
// from the corpus glossary's `member_refs`, which exist only after Pass 2
// reconciliation has run — and `auto_reconcile` swallows its own failures by
// design, so a fully published corpus can still have none.
//
// An un-reconciled corpus therefore gets its OWN empty state. "No concept spans
// two documents" and "cross-document reconciliation has not run" are opposite
// claims, and only the first is a reason to publish another document.

export const RADIUS_TITLE = "Impact radius";

export const RADIUS_SUBTITLE =
  "Concepts your documents share, and how far a change to one would reach.";

export const RADIUS_UNRECONCILED_HEADLINE =
  "Cross-document reconciliation has not run";
export const RADIUS_UNRECONCILED_BODY =
  "The radius is built from the corpus glossary, which Pass 2 writes when it reconciles concepts across your published documents. It has not produced one for this corpus yet, so there is nothing to measure — which is not the same as nothing being shared.";

export const RADIUS_EMPTY_HEADLINE = "Nothing spans two documents yet";
export const RADIUS_EMPTY_BODY =
  "The radius counts the documents that would have to change with a concept. It needs a concept that appears in more than one of them.";

export const DROPOFF_EMPTY_HEADLINE = "No section views recorded yet";
export const DROPOFF_EMPTY_BODY =
  "The map follows visits from one section to the next. It needs a page whose headings carry ids — the widget keys on those, and a page without them reports no sections at all.";

// ---- B3, prerequisite debt (PRD 16 §5) -------------------------------------
//
// "Your Getting Started page requires four levels of undefined prior
// knowledge." A time-to-competence measure, and the one view here that reaches
// the L&D and academic buyer the usage leaderboard never does.
//
// Three wording rules, all of which the data forces:
//
//  1. **It says "assumes", not "requires the reader to learn".** The DAG
//     records what the writing leans on. It cannot know what a reader knew
//     already, and there is no per-reader anything in this product.
//  2. **"Nothing defines this" and "another page defines this" stay apart.**
//     They are different faults with different fixes — write the definition, or
//     bring it onto the page — and one summed "undefined" number would hide
//     both.
//  3. **A capped chain says "or more".** Depth is bounded at 4, so a document
//     that is genuinely six deep would otherwise be reported as tied with one
//     that is exactly four — flattening the worst case this view exists to
//     find.

export const PREREQ_TITLE = "Prerequisite depth";

export const PREREQ_SUBTITLE =
  "How many layers of prior knowledge a page assumes before its own subject starts.";

/** On the depth's `title`. Names what a level is, because "4 levels" invites
 *  being read as "4 terms". */
export const PREREQ_DEPTH_WHY =
  "The longest chain of prerequisites beneath this concept — each level is a concept that has to be understood before the one above it. Not a count of its immediate prerequisites.";

/** Suffix for a chain that was still descending at the cap. */
export const PREREQ_OR_MORE = "or more";

export const PREREQ_LEVELS = "levels deep";
export const PREREQ_ASSUMES_NOTHING = "assumes nothing";

/** The two kinds of missing definition. Never joined into one total. */
export const PREREQ_UNRESOLVED_LABEL = "not defined anywhere";
export const PREREQ_UNRESOLVED_WHY =
  "Named as required prior knowledge, and matching no concept in your corpus. Nothing in your documentation defines it.";
export const PREREQ_OFF_DOC_LABEL = "defined on another page";
export const PREREQ_OFF_DOC_WHY =
  "Defined, but not on this page — the reader has to leave to find it. A navigation cost, not a gap; a different problem from a term nothing defines.";

/** A prerequisite loop. There is no bottom to the chain, so there is no depth. */
export const PREREQ_NO_DEPTH_WHY =
  "This concept's prerequisites form a loop, so there is no bottom to the chain to count to. A depth here would be invented, not measured.";

export const PREREQ_UNRECONCILED_HEADLINE =
  "Cross-document reconciliation has not run";
export const PREREQ_UNRECONCILED_BODY =
  "Prerequisite chains are built from the corpus glossary, which Pass 2 writes when it reconciles concepts across your published documents. It has not produced one for this corpus yet — which is not the same as your pages assuming no prior knowledge.";

export const PREREQ_EMPTY_HEADLINE = "Nothing assumes prior knowledge yet";
export const PREREQ_EMPTY_BODY =
  "A chain appears when one concept's definition names another as a prerequisite. None of the concepts on this page name one.";

// ---- B4, the term coupling graph (PRD 16 §5) -------------------------------
//
// "Readers who look up `webhook` look up `idempotency key` within 40 seconds,
// 8x base rate." The only panel on this screen computed from behaviour rather
// than corpus text — and the one where a false number is most persuasive,
// because a lift is a ratio of a ratio and two rare terms meeting once by
// accident produce a lift in the hundreds.
//
// Four rules, and each one is a claim the data cannot support if broken:
//
//  1. **It says VISITS, and it means one tab.** A co-occurrence is two look-ups
//     inside one session id. It is not one person, and it can never span two
//     visits — there is no reader identifier here and there is not going to be
//     one. "Readers who look up X also look up Y later" is permanently
//     unanswerable and must never be implied.
//  2. **"Together", never "then".** The rollup keys the pair alphabetically and
//     stores an absolute gap, so nothing knows which term came first.
//  3. **Every lift shows the base rate it is a multiple of.** "8x" alone is not
//     a claim.
//  4. **Below the floor there is NO lift** — the backend's reason, rendered,
//     never a 0. A lift of 1.0 already means "no association".

export const COUPLING_TITLE = "Looked up together";

export const COUPLING_SUBTITLE =
  "Pairs of terms one visit opened together, and how much more often that happened than chance alone would produce.";

/** On the lift's `title`. */
export const COUPLING_LIFT_WHY =
  "How many times more often one visit opened both terms than the two terms' own traffic would produce by chance. Computed over the days the pair actually co-occurred, within a single visit.";

/** The qualifier that must accompany the pairing everywhere it is rendered. */
export const COUPLING_ONE_VISIT =
  "in one visit — mAiSight cannot follow a reader between visits and does not try";

export const COUPLING_EXPECTED_LABEL = "expected by chance";
export const COUPLING_TOGETHER_LABEL = "opened both";

/** The gap between the two look-ups. NOT a dwell, and the label says so. */
export const COUPLING_GAP_WHY =
  "Typical time between the two look-ups inside a visit. It says how close together they were, not how long either was read — and it carries no order, so neither term is 'first'.";

export const COUPLING_COUPLED = "opened together far more often than chance";
export const COUPLING_INDEPENDENT = "about as often as chance";

/** Why a pair shows no lift. Prefixed to the backend's own reason; the floors
 *  live in the backend and are never re-derived here. */
export const COUPLING_NO_VERDICT_WHY =
  "Not enough traffic to report a rate — a lift off a handful of visits is noise:";

/** The pairs that are not on this list at all.
 *
 *  A pair only a few visits opened is dropped by the API before a row exists,
 *  not shown with a blank rate: at two or three visits the pair comes close to
 *  describing one visit's reading. Only the COUNT of what was dropped reaches
 *  the browser, which is what keeps the tail visible without naming any of it.
 *  Stated on the panel so a short list never reads as a complete one. */
export const COUPLING_SUPPRESSED_WHY =
  "Pairs opened by only a handful of visits are left out entirely rather than shown with a blank rate — at that size a pair comes close to describing one visit's reading. Only the count of what was left out is sent to your browser.";

export const COUPLING_EMPTY_HEADLINE = "No pair has been opened together yet";
export const COUPLING_EMPTY_BODY =
  "A pair appears when one visit opens two different terms on the same page. This needs a document whose readers look up more than one thing.";

// ---- B5, the Defined x Used quadrant (PRD 16 §5) ---------------------------
//
// PRD 16 asks for two failure modes on one chart. ONE OF THEM CANNOT BE
// MEASURED, and this is the copy that says so rather than filling the corner.
//
// A hover fires only on a `<mark>`, and a mark exists only where a definition
// exists — so a reader stalling on a word the glossary never covered emits
// nothing at all. There is no signal for demand the glossary never met, and
// there is no way to add one without a new event. Estimating it would be the
// most damaging fabrication on this screen, because it is the corner a buyer
// acts on by writing content.
//
// What IS exact is the other corner: a defined term nobody opened, on a page
// with real traffic, is glossary bloat. And "nobody opened it" only means
// something once the page has been read — which is why an unread page reports
// nothing here rather than reporting its whole glossary as dead.

export const QUADRANT_TITLE = "Defined vs opened";

export const QUADRANT_SUBTITLE =
  "Every term this page defines, against what visits actually opened.";

export const QUADRANT_BLOAT_LABEL = "defined, never opened";
export const QUADRANT_BLOAT_WHY =
  "Defined in the glossary and not opened by a single visit while this page had real traffic. Candidates to cut.";

export const QUADRANT_WORKING_LABEL = "defined and opened";

export const QUADRANT_LOST_LABEL = "opened, no longer defined";
export const QUADRANT_LOST_WHY =
  "Visits opened this term and the published page no longer defines it — the definition was removed, or the term was re-extracted under a new id. This is not unmet demand; it is a definition that went away underneath readers.";

/** The corner that does not exist, stated as a fact about the instrument.
 *  Rendered on the panel, not hidden in a tooltip.
 *
 *  There is deliberately no matching BODY constant here: the explanation is
 *  `undefined_demand_reason` off the payload, so the sentence a customer reads
 *  is the same one the API returns and the two cannot drift into disagreeing
 *  about what the product can see. */
export const QUADRANT_UNMEASURABLE_HEADLINE =
  "The fourth corner cannot be measured, and is not estimated";

/** The nearest honest neighbour: a corpus-TEXT signal, labelled as one. */
export const QUADRANT_SINGLETON_LEAD =
  "The closest available lead is jargon your own prose uses once and never explains — found by reading the documents, not by watching anyone.";
export const QUADRANT_SINGLETON_LABEL = "used once, never explained";

/** Why the panel shows no share. Prefixed to the backend's own reason. */
export const QUADRANT_NO_VERDICT_WHY =
  "Not enough visits to this page to call an unopened term unused:";

export const QUADRANT_UNPUBLISHED_HEADLINE = "This page has no published glossary";
export const QUADRANT_UNPUBLISHED_BODY =
  "With nothing defined, every term readers opened would land in 'no longer defined' and the panel would say nothing. Publish a glossary for this document and it fills in.";

export const QUADRANT_EMPTY_HEADLINE = "No term traffic on this page yet";
export const QUADRANT_EMPTY_BODY =
  "The quadrant needs a published glossary and visits that opened at least one term on the page it defines.";

// ---- C4, the within-session learning curve (PRD 16 §6) ---------------------
//
// PRD 16 calls C4 universal: it is the product proving its own value out of the
// customer's own data. That makes it the panel a customer forwards, and the one
// where a fabricated number does the most damage.
//
// THE COPY HAS TO CARRY THREE FACTS THE NUMBERS CANNOT.
//
// 1. Over history there is NO curve. Every figure derives from `term_dwell`,
//    which nothing emitted before 2026-08-16, so a window reaching into the
//    past has nothing in it. Zero would say "readers needed no glossary time";
//    the truth is "nothing was measured", and they are opposite claims.
// 2. It measures RE-READING inside ONE VISIT. A reader who understood a term
//    first time never re-opens it and is invisible here. And a visit is one
//    browser tab — "readers were faster when they came back next week" is
//    permanently unanswerable and must never be implied.
// 3. The denominator is term RE-OPENINGS, not visits and not people. One visit
//    that re-opened three terms contributes three.

export const LEARNING_TITLE = "Second look";

export const LEARNING_SUBTITLE =
  "When a visit opens the same term twice, is the second look quicker than the first?";

/** On the headline share's `title`. */
export const LEARNING_SHARE_WHY =
  "The share of re-openings where the later look was shorter than the first, inside the same visit. Roughly half would come out shorter by chance alone, so only a clear majority counts as readers getting quicker.";

/** The qualifier that must accompany every number on this panel. */
export const LEARNING_ONE_VISIT =
  "within one visit — mAiSight cannot follow a reader between visits and does not try";

/** The unit, spelled out. Never "readers", never "visits". */
export const LEARNING_UNIT = "term re-openings";

export const LEARNING_FASTER = "quicker on the second look";
export const LEARNING_NO_CHANGE = "no quicker on the second look";

/** On the two durations. They are approximations and the label says so. */
export const LEARNING_TYPICAL_WHY =
  "Typical first and later look, combined from one figure per day — an approximation, not an exact median of every look.";

/** Why a term shows no share. Prefixed to the backend's own reason; the floor
 *  lives in the backend and is never re-derived here. */
export const LEARNING_NO_VERDICT_WHY =
  "Not enough re-openings to report a share — a curve off a handful is noise:";

/** Why a measured term can still show no durations.
 *
 *  The backend refuses to compute a first-versus-later pair for any day too few
 *  visits contributed to, because at that size the pair IS one visit's reading
 *  trajectory. It is withheld at write time, so the number does not exist
 *  anywhere rather than being hidden from the response. The share above it is a
 *  sum of counts and is unaffected. */
export const LEARNING_NO_TIMES_WHY =
  "Times are not shown for days too few visits contributed to — at that size a first-and-later pair is one visit's reading rather than a pattern. The share is counted from every re-opening and is unaffected.";

/** The sticky terms — the only actionable rows on this panel. */
export const LEARNING_STICKY_LEAD =
  "Slowest to sink in first. A term readers never get quicker on is a definition that is not landing.";

/** THE SENTENCE THIS PANEL EXISTS TO GET RIGHT.
 *
 *  Headline only; the explanation is `not_measured_reason` off the payload, so
 *  the sentence a customer reads is the one the API returns and the two cannot
 *  drift into disagreeing about what the product can see. */
export const LEARNING_UNMEASURED_HEADLINE = "There is no curve to draw yet";

/** Stated on the unmeasured state, because "no data" invites the wrong reading.
 *  A flat line at zero is a claim; an absent line is not. */
export const LEARNING_UNMEASURED_NOTE =
  "This is an absence of measurement, not a measurement of zero. Hover time has only been recorded since 16 August 2026, so anything before that is blank rather than flat.";

// ---- corpus selection ------------------------------------------------------
//
// An org can own more than one corpus, and the panels below read exactly one at
// a time. The words here have to make that visible without turning a detail of
// our storage layout into the customer's problem: they picked "corpus" when
// they connected a source, so it is their word, not ours.

export const CORPUS_PICKER_LABEL = "Corpus";

/** An unreconciled corpus stays selectable — see `CorpusPicker.tsx`. */
export const CORPUS_UNRECONCILED_SUFFIX = "(not reconciled)";

// ---- X2, the clock ---------------------------------------------------------
//
// THE ONE SENTENCE THIS PANEL CANNOT GET WRONG IS THE TIMEZONE. Buckets are
// UTC, because no reader timezone is recorded and none ever will be. A dial
// labelled "9am" with nothing qualifying it claims we know when a reader's
// morning is, which is a statement about people, not about traffic.
//
// The second is flatness. An even spread is a RESULT — readers are not
// concentrated in any part of the day — and it looks exactly like a broken
// chart. The panel states it in words off the backend's own verdict.

export const CLOCK_TITLE = "When the glossary is used";

export const CLOCK_SUBTITLE =
  "Look-ups folded onto a repeating cycle. Times are UTC: no reader timezone is recorded, so this is when we saw the traffic, not anyone's local morning.";

export const CLOCK_CYCLE_LABEL = "Cycle";
export const CLOCK_CYCLE_DAY = "Hour of day";
export const CLOCK_CYCLE_WEEK = "Day of week";

export const CLOCK_GRAIN_LABEL = "Granularity";
export const CLOCK_GRAIN_HOUR = "1h";
export const CLOCK_GRAIN_QUARTER = "15m";
export const CLOCK_GRAIN_MINUTE = "1m";

/** Why the minute step is drawn and refused rather than hidden.
 *
 *  Measured, not asserted: at minute resolution the term rollup holds 23,137
 *  rows for 31,127 look-ups, so three quarters of the buckets would contain a
 *  single event. Hiding the step would leave a customer wondering whether the
 *  product can do it; refusing it with the measurement answers the question. */
export const CLOCK_GRAIN_MINUTE_WHY =
  "Minute resolution is not offered: at that grain most buckets hold a single look-up, so the chart would report individual events rather than a pattern.";

/** Cycle-independent: applies to hour-of-day and day-of-week alike. */
export const CLOCK_EVEN =
  "Look-ups are spread evenly across the cycle — no part of it is busier than the rest. That is a finding, not an empty chart.";

export const CLOCK_PEAKED = "Look-ups concentrate in part of the cycle.";

export const CLOCK_TERM_RING_LEAD =
  "The dashed outline is this term against the whole corpus, on the same scale.";

/** Why some slots of the term ring are blank. Prefixed to the found/measured
 *  count; the floor lives in the backend and is never re-derived here. */
export const CLOCK_TERM_WITHHELD_WHY =
  "Slots too few visits opened this term in are left blank rather than drawn at zero — at that size a slot is one visit's reading:";

export const CLOCK_EMPTY_HEADLINE = "No clock to draw yet";

// ---- X6, the per-document ridgeline ---------------------------------------

export const RIDGE_TITLE = "Every document, over the window";

/** The shared scale is the point, and a reader has to be told it is shared —
 *  otherwise a quiet document that fills its own row reads as a busy one. */
export const RIDGE_SUBTITLE =
  "One row per document, all on the same scale, busiest first. A row that barely lifts off its line is a document almost nobody reads — not a quiet one drawn small.";

export const RIDGE_EMPTY_HEADLINE = "No traffic in this window";

export const RIDGE_EMPTY_BODY =
  "Nothing has been read in the range selected. A document nobody visits sends no events, which is not the same as an install that is not working.";

// ---- X4, the concept x document matrix ------------------------------------

export const MATRIX_TITLE = "Which concepts reach which documents";

export const MATRIX_SUBTITLE =
  "One row per canonical concept, one column per document, a mark where the concept is defined there. Concepts with the widest reach are at the top.";

/** THE FILL RATE IS NOT DECORATION. Measured at about 3% on a real corpus, and
 *  an unstated sparse grid reads as "no data" rather than as the finding —
 *  most concepts are local to one document, a few span everything. */
export function matrixFill(cells: number, rows: number, columns: number): string {
  const total = rows * columns;
  const pct = total > 0 ? Math.round((cells / total) * 1000) / 10 : 0;
  return `${cells.toLocaleString("en-US")} of ${total.toLocaleString("en-US")} possible pairings are used (${pct}%). A sparse grid is the expected shape: most concepts belong to one document.`;
}

export const MATRIX_EMPTY_HEADLINE = "No concepts span this corpus yet";

// ---- X3, the corpus packing ------------------------------------------------

export const PACK_TITLE = "How the vocabulary packed together";

export const PACK_SUBTITLE =
  "Each circle is one canonical concept; the circles inside it are the document definitions that merged into it. Dashed circles held only one definition — nothing merged into them.";

export const PACK_EMPTY_HEADLINE = "Nothing has been reconciled yet";

export const PACK_EMPTY_BODY =
  "Pass 2 has not produced a canonical set for this corpus. That is a different state from a corpus whose concepts are all local — publish a second document and it fills in on the next reconcile.";
